export interface PatDTO {
  id: string;
  name: string;
  scopes: string[];
  createdAt: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

export interface CreatePatRequest {
  name: string;
  scopes: string[];
  expiresAt: string | null;
}

/** Token for an agent account, issued by the platform admin; unlike a personal token the expiry is required. */
export interface CreateAgentTokenRequest {
  name: string;
  scopes: string[];
  expiresAt: string;
}

/** Upper bound of an agent token's lifetime, enforced by the backend as well. */
export const AGENT_TOKEN_MAX_DAYS = 365;

export interface PatCreatedResponse {
  token: string;
  pat: PatDTO;
}

const NON_SELECTABLE_PAT_SCOPES: readonly string[] = ['tenant:admin', 'project:admin', 'project:manage_members'];

/** Scopes only the platform admin grants to agent accounts; {@code session:create} opens a browser session on the test stage. */
const AGENT_ONLY_PAT_SCOPES: readonly string[] = ['session:create'];

export function selectablePatScopes(catalog: readonly string[]): string[] {
  return selectableAgentPatScopes(catalog).filter((scope) => !AGENT_ONLY_PAT_SCOPES.includes(scope));
}

export function selectableAgentPatScopes(catalog: readonly string[]): string[] {
  return catalog.filter((scope) => !NON_SELECTABLE_PAT_SCOPES.includes(scope));
}

/**
 * Access level a token may hold for a single resource. {@code write} implies
 * {@code read} because every write endpoint also needs read access; the level
 * model therefore makes a write-without-read combination unreachable.
 */
export type PatAccessLevel = 'none' | 'read' | 'write';

export interface PatScopeGroup {
  resource: string;
  supportsRead: boolean;
  supportsWrite: boolean;
  extraActions: readonly string[];
}

/**
 * Folds the flat {@code resource:action} scope list into one group per resource
 * so the UI can offer a single access level (none/read/write) plus any extra
 * actions (e.g. {@code requirement:link}) instead of independent checkboxes.
 */
export function groupSelectablePatScopes(scopes: readonly string[]): PatScopeGroup[] {
  const order: string[] = [];
  const byResource = new Map<string, { read: boolean; write: boolean; extras: string[] }>();

  for (const scope of scopes) {
    const [resource, action] = scope.split(':');
    if (!resource || !action) continue;
    let entry = byResource.get(resource);
    if (!entry) {
      entry = { read: false, write: false, extras: [] };
      byResource.set(resource, entry);
      order.push(resource);
    }
    if (action === 'read') entry.read = true;
    else if (action === 'write') entry.write = true;
    else entry.extras.push(action);
  }

  return order.map((resource) => {
    const entry = byResource.get(resource)!;
    return {
      resource,
      supportsRead: entry.read,
      supportsWrite: entry.write,
      extraActions: entry.extras,
    };
  });
}

/** Flat scopes granted by an access level for a resource. */
export function scopesForLevel(resource: string, level: PatAccessLevel): string[] {
  if (level === 'read') return [`${resource}:read`];
  if (level === 'write') return [`${resource}:read`, `${resource}:write`];
  return [];
}

const LEVEL_ORDER: readonly PatAccessLevel[] = ['none', 'read', 'write'];

/** Whether {@code level} grants at least {@code minimum} access. */
export function isLevelAtLeast(level: PatAccessLevel, minimum: PatAccessLevel): boolean {
  return LEVEL_ORDER.indexOf(level) >= LEVEL_ORDER.indexOf(minimum);
}

/** The more permissive of the two levels. */
export function higherLevel(a: PatAccessLevel, b: PatAccessLevel): PatAccessLevel {
  return LEVEL_ORDER.indexOf(a) >= LEVEL_ORDER.indexOf(b) ? a : b;
}

/**
 * Extra actions that only work if another resource also has a minimum access
 * level, because the backend endpoint they call is guarded by both
 * permissions. {@code requirement:link} calls the coverage endpoints in
 * RequirementCoverageController, which require REQUIREMENT_LINK *and*
 * TESTCASE_READ — without the latter, a token would still get a 403 (NEX-630).
 */
export const EXTRA_ACTION_DEPENDENCIES: Readonly<Record<string, { resource: string; level: PatAccessLevel }>> = {
  'requirement:link': { resource: 'testcase', level: 'read' },
};

export const KONTO_RECHTE_REIHENFOLGE: readonly string[] = [
  'project:read',
  'project:write',
  'requirement:read',
  'requirement:write',
  'requirement:link',
  'testcase:read',
  'testcase:write',
  'testmodule:read',
  'testmodule:write',
  'testplan:read',
  'testplan:write',
  'release:manage',
];

export function kontoRechteChips(catalog: readonly string[]): string[] {
  const waehlbar = selectablePatScopes(catalog);
  const bekannt = KONTO_RECHTE_REIHENFOLGE.filter((scope) => waehlbar.includes(scope));
  return [...bekannt, ...waehlbar.filter((scope) => !KONTO_RECHTE_REIHENFOLGE.includes(scope))];
}

const SCHREIB_AKTIONEN: readonly string[] = ['write', 'manage', 'link'];

export function istSchreibrecht(scope: string): boolean {
  return SCHREIB_AKTIONEN.includes(scope.split(':')[1] ?? '');
}

export type TokenAblauf = 30 | 90 | 365 | null;

export function ablaufZeitpunkt(tage: TokenAblauf, heute: Date): string | null {
  if (tage === null) return null;
  const ablauf = new Date(heute.getFullYear(), heute.getMonth(), heute.getDate() + tage);
  ablauf.setHours(23, 59, 59, 0);
  return ablauf.toISOString();
}
